import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

declare global {
  interface Window {
    umami?: {
      track: (event: string, data?: Record<string, unknown>) => void;
    };
  }
}

export default class extends Controller {
  static targets = ["key"]

  declare readonly keyTarget: HTMLInputElement
  private modal: HTMLDialogElement | null = null
  private modalCloseHandler: (() => void) | null = null

  connect() {
    window.umami?.track("api-key-created")

    // Find the parent modal
    this.modal = this.element.closest('dialog') as HTMLDialogElement

    // Listen for modal close events
    if (this.modal) {
      this.modalCloseHandler = this.handleModalClose.bind(this)
      this.modal.addEventListener('close', this.modalCloseHandler)
    }
  }

  disconnect() {
    // Clean up event listener
    if (this.modal && this.modalCloseHandler) {
      this.modal.removeEventListener('close', this.modalCloseHandler)
    }
  }

  handleModalClose() {
    // Reset the form to initial state by fetching from server
    const turboFrame = this.element.closest('dialog')?.querySelector('turbo-frame#api_key_form') as any
    if (turboFrame) {
      turboFrame.src = '/app/api_keys/new'
    }
  }

  copy(event: Event) {
    event.preventDefault()

    const button = event.currentTarget as HTMLButtonElement
    const originalContent = button.innerHTML

    // Copy to clipboard
    navigator.clipboard.writeText(this.keyTarget.value).then(() => {
      // Show success feedback
      button.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20 6 9 17l-5-5"/>
        </svg>
        Copied!
      `
      button.classList.add("btn-success")

      // Reset after 2 seconds
      setTimeout(() => {
        button.innerHTML = originalContent
        button.classList.remove("btn-success")
      }, 2000)
    }).catch((err) => {
      console.error("Failed to copy:", err)
      window.toast?.("Failed to copy API key", { type: "error" })
    })
  }

  done(event: Event) {
    event.preventDefault()

    // Get the API key ID from the form element
    const apiKeyId = (event.target as HTMLElement).dataset.apiKeyId
    console.log('Done clicked, API key ID:', apiKeyId)

    // Close the modal
    if (this.modal) {
      this.modal.close()
    }

    // Trigger highlight on the newly created element after modal closes
    if (apiKeyId) {
      setTimeout(() => {
        const element = document.getElementById(apiKeyId)
        console.log('Found element:', element)
        if (element) {
          console.log('Dispatching highlight event to:', apiKeyId)
          element.dispatchEvent(new CustomEvent('api-key:highlight', { bubbles: true }))
        } else {
          console.error('Element not found:', apiKeyId)
        }
      }, 100)
    } else {
      console.error('No API key ID found on button')
    }
  }
}
